/**
 * Punch-list b14 — eISF document annotation (highlight/note before
 * sign-off). Replaces the bare native-PDF `<iframe>` in
 * DocumentViewerModal with a pdf.js canvas render plus a highlight/note
 * overlay, mirroring the pdf.js setup already proven in
 * ConsentFieldLayoutEditor.tsx.
 *
 * Visible to everyone who can view the document (no separate
 * private-to-signer tier). Locked (view-only, no draw/delete) once the
 * version is signed — `locked` prop reflects `version.signed_at` plus
 * each annotation's own `locked_at`.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import {
  CheckCircle2, ChevronLeft, ChevronRight, Loader2, Lock,
  MessageSquarePlus, MinusCircle, Trash2, X,
} from 'lucide-react';
// eslint-disable-next-line import/no-unresolved
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { binder } from '@/api/client';
import { errorText } from '@/lib/permissionError';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const SCALE = 1.3;
const RELEVANCE_COLOUR: Record<string, string> = {
  relevant: 'border-emerald-500 bg-emerald-500/15',
  not_relevant: 'border-red-500 bg-red-500/15',
};
const DEFAULT_COLOUR = 'border-amber-500 bg-amber-500/15';

interface DraftBox { page: number; x: number; y: number; width: number; height: number }

export function AnnotatablePdfView({
  binderRef,
  documentRef,
  versionId,
  previewUrl,
  fileName,
  locked,
  canAnnotate,
}: {
  binderRef: string;
  documentRef: string;
  versionId: string;
  previewUrl: string;
  fileName: string;
  locked: boolean;
  canAnnotate: boolean;
}) {
  const qc = useQueryClient();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [drawMode, setDrawMode] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [draftBox, setDraftBox] = useState<DraftBox | null>(null);
  const [pendingSave, setPendingSave] = useState<DraftBox | null>(null);
  const [noteText, setNoteText] = useState('');
  const [relevance, setRelevance] = useState<'relevant' | 'not_relevant' | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Load the PDF document once per preview URL.
  useEffect(() => {
    let cancelled = false;
    setPdfDoc(null);
    (async () => {
      try {
        const task = pdfjsLib.getDocument({ url: previewUrl });
        const doc = await task.promise;
        if (cancelled) return;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setPageNum(1);
      } catch (err) {
        if (!cancelled) console.warn('[AnnotatablePdfView] load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [previewUrl]);

  // Render the current page into the canvas.
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;
    let renderTask: RenderTask | null = null;
    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNum);
        if (cancelled || !canvasRef.current) return;
        const viewport = page.getViewport({ scale: SCALE });
        const canvas = canvasRef.current;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        renderTask = page.render({ canvas, canvasContext: ctx, viewport });
        await renderTask.promise;
      } catch (err) {
        const msg = (err as Error)?.message ?? '';
        if (!/cancel|abort/i.test(msg)) console.warn('[AnnotatablePdfView] page render failed:', err);
      }
    })();
    return () => {
      cancelled = true;
      try { renderTask?.cancel(); } catch { /* noop */ }
    };
  }, [pdfDoc, pageNum]);

  const annotationsQ = useQuery({
    queryKey: ['document-annotations', binderRef, documentRef, versionId],
    queryFn: () => binder.listAnnotations(binderRef, documentRef, versionId),
  });
  const annotations = annotationsQ.data?.annotations ?? [];
  const pageAnnotations = annotations.filter((a) => a.page === pageNum);

  const createMut = useMutation({
    mutationFn: (body: DraftBox & { note_text?: string; relevance?: 'relevant' | 'not_relevant' }) =>
      binder.createAnnotation(binderRef, documentRef, versionId, body),
    onSuccess: () => {
      toast.success('Annotation added');
      setPendingSave(null);
      setDraftBox(null);
      setNoteText('');
      setRelevance(undefined);
      qc.invalidateQueries({ queryKey: ['document-annotations', binderRef, documentRef, versionId] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to save annotation')),
  });

  const deleteMut = useMutation({
    mutationFn: (annotationId: string) => binder.deleteAnnotation(binderRef, documentRef, versionId, annotationId),
    onSuccess: () => {
      toast.success('Annotation removed');
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: ['document-annotations', binderRef, documentRef, versionId] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to remove annotation')),
  });

  function relativePos(clientX: number, clientY: number): { x: number; y: number } | null {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  }

  function handleMouseDown(e: React.MouseEvent) {
    if (!drawMode || locked) return;
    const pos = relativePos(e.clientX, e.clientY);
    if (!pos) return;
    setDragStart(pos);
    setDraftBox({ page: pageNum, x: pos.x, y: pos.y, width: 0, height: 0 });
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (!drawMode || !dragStart) return;
    const pos = relativePos(e.clientX, e.clientY);
    if (!pos) return;
    const x = Math.min(dragStart.x, pos.x);
    const y = Math.min(dragStart.y, pos.y);
    const width = Math.abs(pos.x - dragStart.x);
    const height = Math.abs(pos.y - dragStart.y);
    setDraftBox({ page: pageNum, x, y, width, height });
  }

  function handleMouseUp() {
    if (!drawMode || !dragStart) return;
    setDragStart(null);
    if (draftBox && draftBox.width > 0.01 && draftBox.height > 0.01) {
      setPendingSave(draftBox);
    } else {
      setDraftBox(null);
    }
  }

  function saveAnnotation() {
    if (!pendingSave) return;
    createMut.mutate({ ...pendingSave, note_text: noteText.trim() || undefined, relevance });
  }

  function cancelDraft() {
    setDraftBox(null);
    setPendingSave(null);
    setNoteText('');
    setRelevance(undefined);
  }

  const selected = annotations.find((a) => a.annotation_id === selectedId) ?? null;

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-surface-border bg-surface-card px-3 py-1.5">
        <div className="flex items-center gap-2 text-xs text-ink-secondary">
          <button
            type="button"
            className="rounded p-1 hover:bg-surface-muted disabled:opacity-40"
            disabled={pageNum <= 1}
            onClick={() => setPageNum((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="tabular-nums">Page {pageNum} of {numPages || '—'}</span>
          <button
            type="button"
            className="rounded p-1 hover:bg-surface-muted disabled:opacity-40"
            disabled={pageNum >= numPages}
            onClick={() => setPageNum((p) => Math.min(numPages, p + 1))}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
        {locked ? (
          <span className="flex items-center gap-1 text-[11px] text-ink-muted">
            <Lock className="h-3 w-3" /> Signed — annotations locked
          </span>
        ) : canAnnotate ? (
          <button
            type="button"
            onClick={() => { setDrawMode((v) => !v); cancelDraft(); }}
            className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${
              drawMode ? 'bg-accent-600 text-white' : 'border border-surface-border text-ink-secondary hover:bg-surface-muted/50'
            }`}
          >
            <MessageSquarePlus className="h-3.5 w-3.5" /> {drawMode ? 'Drawing — click-drag a region' : 'Annotate'}
          </button>
        ) : null}
      </div>

      <div ref={containerRef} className="relative flex-1 overflow-auto bg-surface-muted/30 p-4">
        {!pdfDoc && (
          <div className="flex h-full items-center justify-center text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        <div className="relative mx-auto inline-block">
          <canvas ref={canvasRef} className="block bg-white shadow" />
          <div
            ref={overlayRef}
            className={`absolute inset-0 ${drawMode && !locked ? 'cursor-crosshair' : ''}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            {pageAnnotations.map((a) => (
              <div
                key={a.annotation_id}
                onClick={(e) => { e.stopPropagation(); setSelectedId(a.annotation_id); }}
                className={`absolute cursor-pointer rounded border-2 ${RELEVANCE_COLOUR[a.relevance ?? ''] ?? DEFAULT_COLOUR} ${
                  selectedId === a.annotation_id ? 'ring-2 ring-accent-500' : ''
                }`}
                style={{
                  left: `${a.x * 100}%`,
                  top: `${a.y * 100}%`,
                  width: `${a.width * 100}%`,
                  height: `${a.height * 100}%`,
                }}
                title={a.note_text ?? undefined}
              />
            ))}
            {draftBox && draftBox.page === pageNum && (
              <div
                className="pointer-events-none absolute rounded border-2 border-dashed border-accent-500 bg-accent-500/10"
                style={{
                  left: `${draftBox.x * 100}%`,
                  top: `${draftBox.y * 100}%`,
                  width: `${draftBox.width * 100}%`,
                  height: `${draftBox.height * 100}%`,
                }}
              />
            )}
          </div>
        </div>
      </div>

      {pendingSave && (
        <div className="border-t border-surface-border bg-surface-card p-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold text-ink-primary">New annotation</h4>
            <button type="button" onClick={cancelDraft}><X className="h-3.5 w-3.5 text-ink-muted" /></button>
          </div>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            maxLength={4000}
            rows={2}
            placeholder="Note (optional)"
            className="input mt-2 w-full text-xs"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setRelevance(relevance === 'relevant' ? undefined : 'relevant')}
                className={`flex items-center gap-1 rounded border px-2 py-1 text-[11px] ${
                  relevance === 'relevant' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-surface-border text-ink-secondary'
                }`}
              >
                <CheckCircle2 className="h-3 w-3" /> Relevant
              </button>
              <button
                type="button"
                onClick={() => setRelevance(relevance === 'not_relevant' ? undefined : 'not_relevant')}
                className={`flex items-center gap-1 rounded border px-2 py-1 text-[11px] ${
                  relevance === 'not_relevant' ? 'border-red-500 bg-red-50 text-red-700' : 'border-surface-border text-ink-secondary'
                }`}
              >
                <MinusCircle className="h-3 w-3" /> Not relevant
              </button>
            </div>
            <button
              type="button"
              disabled={createMut.isPending}
              onClick={saveAnnotation}
              className="rounded bg-accent-600 px-3 py-1 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-60"
            >
              {createMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save annotation'}
            </button>
          </div>
        </div>
      )}

      {selected && !pendingSave && (
        <div className="border-t border-surface-border bg-surface-card p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {selected.relevance && (
                  <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                    selected.relevance === 'relevant' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {selected.relevance === 'relevant' ? 'Relevant' : 'Not relevant'}
                  </span>
                )}
                <span className="text-[10px] text-ink-muted">Page {selected.page}</span>
              </div>
              <p className="mt-1 text-xs text-ink-primary">{selected.note_text || <span className="italic text-ink-muted">No note</span>}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {!selected.locked_at && !locked && canAnnotate && (
                <button
                  type="button"
                  onClick={() => deleteMut.mutate(selected.annotation_id)}
                  disabled={deleteMut.isPending}
                  className="text-ink-muted hover:text-danger-700"
                  aria-label="Delete annotation"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
              <button type="button" onClick={() => setSelectedId(null)}><X className="h-3.5 w-3.5 text-ink-muted" /></button>
            </div>
          </div>
        </div>
      )}

      {annotations.length > 0 && !selected && !pendingSave && (
        <div className="border-t border-surface-border bg-surface-card px-3 py-1.5 text-[11px] text-ink-muted">
          {annotations.length} annotation{annotations.length === 1 ? '' : 's'} on this document — click a highlighted region to view it. {fileName}
        </div>
      )}
    </div>
  );
}
